import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getLoanStatement, downloadLoanStatement } from '../../../services/loanService';
import type { LoanStatement } from '../../../services/schemas/loanServiceSchemas/loanStatement';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { toLocalDateString } from '../../../utils/localDate';

interface Props {
  accountId?: string;
  accountNumber?: string;
  /** The loan's disbursement date, for the "Since disbursement" period. */
  disbursementDate?: string;
  show: boolean;
  onHide: () => void;
}

type Period = 'lastMonth' | 'thisMonth' | 'last3Months' | 'sinceDisbursement' | 'custom';

const periodRange = (period: Period, disbursementDate?: string): { from: string; to: string } => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (period) {
    case 'thisMonth':
      return { from: toLocalDateString(new Date(y, m, 1)), to: toLocalDateString(today) };
    case 'last3Months':
      return { from: toLocalDateString(new Date(y, m - 3, 1)), to: toLocalDateString(new Date(y, m, 0)) };
    case 'sinceDisbursement':
      return { from: (disbursementDate || '').slice(0, 10) || toLocalDateString(new Date(y, m, 1)), to: toLocalDateString(today) };
    case 'lastMonth':
    default:
      return { from: toLocalDateString(new Date(y, m - 1, 1)), to: toLocalDateString(new Date(y, m, 0)) };
  }
};

/**
 * US-33: a client statement for any period - opening balance, every transaction with a running balance,
 * closing balance and what is owed - previewed here and downloaded as a PDF to send to the client.
 */
const LoanStatementModal: React.FC<Props> = ({ accountId, accountNumber, disbursementDate, show, onHide }) => {
  const [period, setPeriod] = useState<Period>('sinceDisbursement');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [statement, setStatement] = useState<LoanStatement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (show) {
      const range = periodRange('sinceDisbursement', disbursementDate);
      setPeriod('sinceDisbursement');
      setFrom(range.from);
      setTo(range.to);
      setStatement(null);
    }
  }, [show, disbursementDate]);

  const choosePeriod = (next: Period) => {
    setPeriod(next);
    if (next !== 'custom') {
      const range = periodRange(next, disbursementDate);
      setFrom(range.from);
      setTo(range.to);
    }
  };

  const periodError = !from || !to ? 'Choose the first and last day of the period' : to < from ? 'The period ends before it starts' : '';

  const load = useCallback(async () => {
    if (!accountId || !from || !to || to < from) return;
    setIsLoading(true);
    try {
      setStatement(await getLoanStatement(accountId, { from, to }));
    } catch (error: any) {
      setStatement(null);
      toast.error(error?.response?.data?.message || error?.message || 'Could not produce the statement');
    } finally {
      setIsLoading(false);
    }
  }, [accountId, from, to]);

  useEffect(() => {
    if (show) load();
  }, [show, load]);

  const download = async () => {
    if (!accountId || periodError) return;
    setIsDownloading(true);
    try {
      const blob = await downloadLoanStatement(accountId, { from, to });
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Statement_${(accountNumber || 'loan').replace(/[^A-Za-z0-9-]/g, '_')}_${from}_${to}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error?.message || 'Could not download the statement');
    } finally {
      setIsDownloading(false);
    }
  };

  const lines = statement?.lines || [];

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-file-text-o me-2 text-primary"></i>
          Loan statement {accountNumber && <small className="text-muted fs-6 ms-2">{accountNumber}</small>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3 align-items-end mb-3">
          <div className="col-md-4">
            <Form.Label className="fw-semibold">Period</Form.Label>
            <Form.Select value={period} onChange={(e) => choosePeriod(e.target.value as Period)}>
              <option value="lastMonth">Last month</option>
              <option value="thisMonth">This month to date</option>
              <option value="last3Months">Last 3 months</option>
              <option value="sinceDisbursement">Since disbursement</option>
              <option value="custom">Custom dates</option>
            </Form.Select>
          </div>
          <div className="col-md-3">
            <Form.Label className="fw-semibold">From</Form.Label>
            <Form.Control type="date" value={from} max={to || undefined}
              onChange={(e) => { setPeriod('custom'); setFrom(e.target.value); }} />
          </div>
          <div className="col-md-3">
            <Form.Label className="fw-semibold">To</Form.Label>
            <Form.Control type="date" value={to} min={from || undefined}
              onChange={(e) => { setPeriod('custom'); setTo(e.target.value); }} />
          </div>
          <div className="col-md-2 text-md-end">
            <Button variant="outline-secondary" onClick={load} disabled={!!periodError || isLoading} className="w-100">
              <i className={`fa fa-refresh me-1 ${isLoading ? 'fa-spin' : ''}`}></i>Refresh
            </Button>
          </div>
        </div>
        {periodError && <div className="text-danger small mb-3">{periodError}</div>}

        {isLoading && !statement ? (
          <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
        ) : statement ? (
          <>
            <div className="d-flex flex-wrap justify-content-between mb-3 small">
              <div>
                <div className="fw-bold fs-6">{statement.customerName || '-'}</div>
                <div className="text-muted">
                  {statement.customerNumber && <>Customer {statement.customerNumber} · </>}
                  {statement.productName || 'Loan'}
                </div>
              </div>
              <div className="text-end text-muted">
                <div>{formatDate(statement.periodFrom)} to {formatDate(statement.periodTo)}</div>
                {statement.disbursementDate && <div>Disbursed {formatDate(statement.disbursementDate)}</div>}
              </div>
            </div>

            <div className="row g-2 mb-3">
              {[
                { label: 'Opening balance', value: statement.openingBalance },
                { label: 'Debits (charges)', value: statement.totalDebits },
                { label: 'Credits (payments)', value: statement.totalCredits },
                { label: 'Closing balance', value: statement.closingBalance, strong: true },
              ].map((card) => (
                <div className="col-6 col-md-3" key={card.label}>
                  <div className={`border rounded p-2 h-100 ${card.strong ? 'bg-success bg-opacity-10' : 'bg-light'}`}>
                    <div className="text-muted small">{card.label}</div>
                    <div className="fw-bold fs-5">{formatCurrency(card.value || 0)}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="table-responsive mb-3">
              <table className="table table-sm align-middle mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Reference</th>
                    <th className="text-end">Debit</th>
                    <th className="text-end">Credit</th>
                    <th className="text-end">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="table-light fw-semibold">
                    <td>{formatDate(statement.periodFrom)}</td>
                    <td colSpan={4}>Opening balance</td>
                    <td className="text-end">{formatCurrency(statement.openingBalance || 0)}</td>
                  </tr>
                  {lines.length === 0 && (
                    <tr><td colSpan={6} className="text-muted text-center py-3">No transactions in this period.</td></tr>
                  )}
                  {lines.map((line, index) => (
                    <tr key={`${line.date}-${index}`}>
                      <td>{formatDate(line.date)}</td>
                      <td>{line.description}</td>
                      <td className="text-muted small">{line.reference}</td>
                      <td className="text-end">{line.debit != null ? formatCurrency(line.debit) : ''}</td>
                      <td className="text-end text-success">{line.credit != null ? formatCurrency(line.credit) : ''}</td>
                      <td className="text-end">{formatCurrency(line.balance || 0)}</td>
                    </tr>
                  ))}
                  <tr className="table-light fw-semibold">
                    <td>{formatDate(statement.periodTo)}</td>
                    <td colSpan={4}>Closing balance</td>
                    <td className="text-end">{formatCurrency(statement.closingBalance || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="table-responsive mb-3">
              <table className="table table-sm table-bordered text-center mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>120+ Days</th><th>90 Days</th><th>60 Days</th><th>30 Days</th><th>Current</th>
                    <th>Amount Due</th><th>Amount Paid</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{formatCurrency(statement.age120PlusDays || 0)}</td>
                    <td>{formatCurrency(statement.age90Days || 0)}</td>
                    <td>{formatCurrency(statement.age60Days || 0)}</td>
                    <td>{formatCurrency(statement.age30Days || 0)}</td>
                    <td>{formatCurrency(statement.ageCurrent || 0)}</td>
                    <td className="fw-bold">{formatCurrency(statement.closingBalance || 0)}</td>
                    <td>{formatCurrency(statement.totalCredits || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="row g-3 small">
              <div className="col-md-6">
                <div className="fw-semibold mb-1">Closing balance made up of</div>
                <div className="d-flex justify-content-between border-bottom py-1"><span>Capital</span><span>{formatCurrency(statement.principalBalance || 0)}</span></div>
                <div className="d-flex justify-content-between border-bottom py-1"><span>Interest</span><span>{formatCurrency(statement.interestBalance || 0)}</span></div>
                <div className="d-flex justify-content-between border-bottom py-1"><span>Fees</span><span>{formatCurrency(statement.feesBalance || 0)}</span></div>
                <div className="d-flex justify-content-between border-bottom py-1"><span>Penalties</span><span>{formatCurrency(statement.penaltiesBalance || 0)}</span></div>
              </div>
              <div className="col-md-6">
                <div className="fw-semibold mb-1">Payments</div>
                <div className="d-flex justify-content-between border-bottom py-1">
                  <span>In arrears at {formatDate(statement.periodTo)}</span>
                  <span className={(statement.arrearsAmount || 0) > 0 ? 'text-danger fw-semibold' : ''}>{formatCurrency(statement.arrearsAmount || 0)}</span>
                </div>
                {statement.nextInstallmentDate && (
                  <div className="d-flex justify-content-between border-bottom py-1">
                    <span>Next instalment, due {formatDate(statement.nextInstallmentDate)}</span>
                    <span>{formatCurrency(statement.nextInstallmentAmount || 0)}</span>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
        <Button variant="primary" onClick={download} disabled={!!periodError || isDownloading || !statement}>
          {isDownloading ? (
            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Preparing...</>
          ) : (
            <><i className="fa fa-download me-2"></i>Download PDF</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LoanStatementModal;
